import { hasRole, type User, type UserRoleWithAcademy } from '@court-sync/shared';

type OrgCheckUser = Pick<User, 'academyId' | 'hasAcademyProfile' | 'roles' | 'rolesWithAcademy'>;

/** User has created an academy and/or club organization entity. */
export function userHasOrgEntity(user: OrgCheckUser): boolean {
  if (user.academyId) return true;

  const scoped = user.rolesWithAcademy ?? [];
  return scoped.some(
    (r) =>
      ((r.role === 'club_owner' || r.role === 'club_admin') && r.clubId != null) ||
      ((r.role === 'academy_owner' || r.role === 'academy_admin') && r.academyId != null)
  );
}

/** Guardian/student/coach with a real academy association (not the empty-role default). */
export function hasAcademyMemberContext(user: OrgCheckUser): boolean {
  if (user.hasAcademyProfile) return true;

  return (
    user.rolesWithAcademy?.some(
      (r) =>
        (r.role === 'guardian' || r.role === 'student' || r.role === 'coach') &&
        r.academyId != null
    ) ?? false
  );
}

/**
 * Platform operators who registered but have not finished org setup (academy/club/both).
 * Excludes academy members who should use the academy picker instead.
 */
export function needsPlatformOrgOnboarding(user: OrgCheckUser | null): boolean {
  if (!user) return false;
  if (hasRole(user, 'admin')) return false;
  if (userHasOrgEntity(user)) return false;
  if (hasAcademyMemberContext(user)) return false;
  if (user.roles?.includes('member')) return false;
  if (user.rolesWithAcademy?.some((r) => r.role === 'member' && r.clubId != null)) return false;
  return true;
}

export type PlatformLoginRedirect =
  | { path: '/academy-setup'; state: { email: string; message: string; isSocialLogin?: boolean } }
  | { path: '/org-picker'; state?: undefined }
  | { path: '/admin/dashboard'; state?: undefined }
  | { path: '/backoffice/risk'; state?: undefined }
  | { path: '/coach/academy-picker'; state?: undefined }
  | { path: '/dashboard'; state?: undefined };

/** Guardian/student/member with org consumer portal access. */
export function hasOrgConsumerContext(user: OrgCheckUser): boolean {
  if (hasAcademyMemberContext(user)) return true;
  if (user.roles?.includes('member')) return true;
  return user.rolesWithAcademy?.some((r) => r.role === 'member' && r.clubId != null) ?? false;
}

export function resolvePlatformDashboardRedirect(user: User): PlatformLoginRedirect {
  if (hasRole(user, 'admin')) {
    return { path: '/backoffice/risk' };
  }

  if (needsPlatformOrgOnboarding(user)) {
    return {
      path: '/academy-setup',
      state: {
        email: user.email,
        message: 'Welcome back! Pick up where you left off setting up your organization.',
      },
    };
  }

  const hasGuardianForAcademy =
    user.rolesWithAcademy?.some(
      (r: UserRoleWithAcademy) =>
        (r.role === 'guardian' || r.role === 'student') && r.academyId != null
    ) ?? false;
  const hasStaleAcademyOwner =
    user.rolesWithAcademy?.some(
      (r: UserRoleWithAcademy) => r.role === 'academy_owner' && r.academyId == null
    ) ?? false;
  const doesNotOwnAcademy = !user.academyId;

  if (
    hasGuardianForAcademy &&
    hasStaleAcademyOwner &&
    doesNotOwnAcademy &&
    (hasAcademyMemberContext(user) || hasRole(user, 'guardian') || hasRole(user, 'student'))
  ) {
    return { path: '/org-picker' };
  }

  if (hasRole(user, 'academy_owner') || hasRole(user, 'academy_admin')) {
    return { path: '/admin/dashboard' };
  }

  if (hasRole(user, 'club_owner') || hasRole(user, 'club_admin')) {
    return { path: '/admin/dashboard' };
  }

  if (hasOrgConsumerContext(user) || hasRole(user, 'guardian') || hasRole(user, 'student') || hasRole(user, 'member')) {
    return { path: '/org-picker' };
  }

  if (hasRole(user, 'coach')) {
    return { path: '/coach/academy-picker' };
  }

  return { path: '/dashboard' };
}
